import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, OnDestroy, OnChanges, Input, SimpleChanges, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../../kuba.services';

import { AppraisalMeetingService } from '../meetings/services/appraisal-meeting.service';
import {
  AppraisalMeetingDto,
  AppraisalMeetingEmployeeProgressStatus,
  AppraisalMeetingListItemDto,
  AppraisalMeetingPaginatedFilterDto,
  AppraisalMeetingQuestionDto,
  AppraisalMeetingRespondentRole,
  AppraisalMeetingResponseSubmitDto,
  AppraisalMeetingStatus
} from '../meetings/models';

type AppraisalMeetingViewScope = 'manager' | 'employee';
type AppraisalArchiveStatusFilter = 'active' | 'archived' | 'all';

interface AppraisalArchiveStatusCounts {
  active: number;
  archived: number;
  all: number;
}

interface AppraisalArchiveStatusOption {
  labelKey: string;
  value: AppraisalArchiveStatusFilter;
  countKey: keyof AppraisalArchiveStatusCounts;
}

interface AppraisalArchiveStatusPalette {
  background: string;
  border: string;
  color: string;
  mutedBorder: string;
  mutedColor: string;
  countBackground: string;
  shadow: string;
}

@Component({
  selector: 'appraisals-list',
  templateUrl: './appraisals-list.component.html',
  styleUrls: ['./appraisals-list.component.scss'],
  standalone: false
})
export class AppraisalsListComponent implements OnInit, OnDestroy, OnChanges {
  //#region Variables
  @Input() isManager: boolean = false;
  @Input() viewScope?: AppraisalMeetingViewScope;
  @Input() initialStatusFilter: string = '';

  private readonly notifications = inject(NotificationService);
  @ViewChild('rowActionMenu', { static: false }) rowActionMenu?: Menu;

  // Data
  appraisals: AppraisalMeetingListItemDto[] = [];
  selectedAppraisals: AppraisalMeetingListItemDto[] = [];
  rowActionItems: MenuItem[] = [];

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  first: number = 0;

  // Loading state
  isLoading: boolean = false;
  exportingAppraisalId: number | null = null;
  deletingAppraisalId: number | null = null;

  // Filters
  employeeNameFilter: string = '';
  templateNameFilter: string = '';
  statusFilter: string = '';
  archiveStatusFilter: AppraisalArchiveStatusFilter = 'active';
  archiveStatusCounts: AppraisalArchiveStatusCounts = {
    active: 0,
    archived: 0,
    all: 0
  };

  // Filter change subject for debouncing
  private filterChangeSubject = new Subject<string>();

  // Sorting
  sortField: string = 'MeetingActionPriority';
  sortOrder: string = 'ASC';

  // Permissions
  canCreateAppraisal: boolean = false;
  canEditAppraisal: boolean = false;
  canDeleteAppraisal: boolean = false;
  canViewDetails: boolean = true;
  // isManager is provided by parent access resolution.

  // Status options
  statusOptions: any[] = [];
  archiveStatusOptions: AppraisalArchiveStatusOption[] = [
    { labelKey: 'APPRAISAL_ACTIVE_MEETINGS', value: 'active', countKey: 'active' },
    { labelKey: 'APPRAISAL_ARCHIVED_MEETINGS', value: 'archived', countKey: 'archived' },
    { labelKey: 'ALL', value: 'all', countKey: 'all' }
  ];

  private subscriptions: Subscription[] = [];
  private archiveCountsSubscription?: Subscription;
  private readonly timezoneSuffixPattern = /(z|[+-]\d{2}:?\d{2})$/i;
  //#endregion

  //#region Constructor
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private meetingService: AppraisalMeetingService
  ) {
    // Setup filter debounce
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.currentPage = 1;
          this.first = 0;
          this.loadAppraisals();
        })
    );
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    if (this.initialStatusFilter) {
      this.statusFilter = this.initialStatusFilter;
    }
    this.initializeStatusOptions();
    this.checkPermissions();
    this.loadAppraisals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isManager']) {
      this.checkPermissions();
    }
    if (changes['initialStatusFilter']) {
      this.statusFilter = this.initialStatusFilter;
    }
    if (['viewScope', 'initialStatusFilter'].some(key => changes[key] !== undefined && !changes[key].firstChange)) {
      this.currentPage = 1;
      this.first = 0;
      this.loadAppraisals();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.archiveCountsSubscription?.unsubscribe();
  }
  //#endregion

  //#region Data Loading
  /**
   * Load appraisals list with filters and pagination
   */
  loadAppraisals(): void {
    this.isLoading = true;

    const filter = this.buildAppraisalFilter();
    this.loadArchiveStatusCounts(filter);

    this.meetingService.getPagedList(BaseServices.BusinessId, filter).subscribe({
      next: response => {
        this.appraisals = response.Items || [];
        this.totalItems = response.TotalCount || 0;
        this.totalPages = response.TotalPages || 0;
        this.updateArchiveStatusCounts(response.Metadata ?? (response as any).metadata);
        this.deletingAppraisalId = null;
        this.isLoading = false;
      },
      error: () => {
        this.deletingAppraisalId = null;
        this.isLoading = false;
        this.showError('Failed to load dialog meetings');
      }
    });
  }
  //#endregion

  //#region Event Handlers
  /**
   * Handle page change event
   */
  onPageChange(event: any): void {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.pageSize = event.rows;
    this.first = event.first;
    this.loadAppraisals();
  }

  /**
   * Handle sort event
   */
  onSort(event: any): void {
    const sortMap: Record<string, string> = {
      Title: 'Title',
      title: 'Title',
      ScheduledDate: 'ScheduledDate',
      scheduledDate: 'ScheduledDate',
      Status: 'Status',
      status: 'Status',
      CreatedOn: 'CreatedOn',
      createdOn: 'CreatedOn'
    };
    this.sortField = sortMap[event.field] || 'CreatedOn';
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';
    this.loadAppraisals();
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.filterChangeSubject.next('filter');
  }

  /**
   * Switch the archive-state view (Active / Archived / All) via the segmented toggle.
   */
  setArchiveStatusFilter(value: AppraisalArchiveStatusFilter): void {
    if (this.archiveStatusFilter === value) {
      return;
    }
    this.archiveStatusFilter = value;
    this.applyArchiveStatusDefaultSort();
    this.onFilterChange();
  }

  /**
   * Clear search/status filters while keeping the selected archive view.
   */
  clearFilters(): void {
    this.employeeNameFilter = '';
    this.templateNameFilter = '';
    this.statusFilter = '';
    this.onFilterChange();
  }

  /**
   * Handle row selection
   */
  onRowSelect(_event: any): void {
    // Selection is only used to enable bulk actions/export context.
  }

  /**
   * Handle row unselection
   */
  onRowUnselect(_event: any): void {
    // Selection is only used to enable bulk actions/export context.
  }
  //#endregion

  //#region Actions
  /**
   * View appraisal details
   */
  viewAppraisal(appraisalId: number): void {
    this.router.navigate(['./meetings', appraisalId], { relativeTo: this.route });
  }

  /**
   * Download the backend-generated dialog meeting report PDF.
   */
  exportAppraisalReport(appraisalId: number): void {
    if (this.exportingAppraisalId !== null) {
      return;
    }

    this.exportingAppraisalId = appraisalId;
    this.meetingService.downloadReport(appraisalId).subscribe({
      next: response => {
        this.exportingAppraisalId = null;
        const blob = response.body;
        if (!blob || blob.size === 0) {
          this.showError('Failed to download dialog meeting report');
          return;
        }

        const fileName = this.getDownloadFileName(response);
        this.triggerBlobDownload(blob, fileName);
      },
      error: (error: Error) => {
        this.exportingAppraisalId = null;
        const message = error?.message || 'Failed to download dialog meeting report';
        this.showError(message);
      }
    });
  }

  /**
   * Create new appraisal
   */
  createAppraisal(): void {
    this.router.navigate(['./meetings/create'], { relativeTo: this.route });
  }

  archiveAppraisal(appraisal: AppraisalMeetingListItemDto): void {
    if (!this.canArchiveAppraisalRow(appraisal) || this.isDeletingAppraisal(appraisal.Id)) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_ARCHIVE_APPRAISAL', { name: appraisal.EmployeeName }),
      accept: () => {
        this.deletingAppraisalId = appraisal.Id;
        this.meetingService.delete(appraisal.Id).subscribe({
          next: () => {
            this.showSuccess('Dialog meeting archived successfully');
            this.loadAppraisals();
          },
          error: () => {
            this.deletingAppraisalId = null;
            this.showError('Failed to archive dialog meeting');
          }
        });
      }
    });
  }

  restoreAppraisal(appraisal: AppraisalMeetingListItemDto): void {
    if (!this.canRestoreAppraisalRow(appraisal) || this.isDeletingAppraisal(appraisal.Id)) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_RESTORE_APPRAISAL', { name: appraisal.EmployeeName }),
      accept: () => {
        this.deletingAppraisalId = appraisal.Id;
        this.meetingService.restore(appraisal.Id).subscribe({
          next: () => {
            this.showSuccess('Dialog meeting restored successfully');
            this.loadAppraisals();
          },
          error: () => {
            this.deletingAppraisalId = null;
            this.showError('Failed to restore dialog meeting');
          }
        });
      }
    });
  }

  openRowActionMenu(menu: Menu, appraisal: AppraisalMeetingListItemDto, event: MouseEvent): void {
    if (this.isDeletingAppraisal(appraisal.Id)) {
      return;
    }

    event.stopPropagation();
    this.rowActionItems = this.buildRowActionItems(appraisal);
    menu.toggle(event);
  }

  private buildRowActionItems(appraisal: AppraisalMeetingListItemDto): MenuItem[] {
    const isExporting = this.exportingAppraisalId === appraisal.Id;
    const isAnyExportInProgress = this.exportingAppraisalId !== null;
    const isRowBusy = this.isDeletingAppraisal(appraisal.Id);

    const viewLabelKey = 'VIEW';
    const viewIcon = appraisal.IsArchived || appraisal.IsReadOnlyForCurrentUser ? 'pi pi-eye' : 'pi pi-pencil';

    const items: MenuItem[] = [
      {
        label: this.translate.instant(viewLabelKey),
        icon: `${viewIcon} kuba-icon-info`,
        disabled: isRowBusy,
        styleClass: 'view-item',
        command: () => this.viewAppraisal(appraisal.Id)
      },
      {
        label: this.translate.instant('EXPORT'),
        icon: isExporting ? 'pi pi-spin pi-spinner kuba-icon-neutral' : 'pi pi-download kuba-icon-neutral',
        disabled: isAnyExportInProgress || isRowBusy,
        styleClass: 'export-item',
        command: () => this.exportAppraisalReport(appraisal.Id)
      }
    ];

    if (this.canArchiveAppraisalRow(appraisal)) {
      items.push({
        label: this.translate.instant('ARCHIVE'),
        icon: 'pi pi-inbox kuba-icon-warn',
        disabled: isRowBusy,
        styleClass: 'archive-item',
        command: () => this.archiveAppraisal(appraisal)
      });
    }

    if (this.canRestoreAppraisalRow(appraisal)) {
      items.push({
        label: this.translate.instant('RESTORE'),
        icon: 'pi pi-refresh kuba-icon-success',
        disabled: isRowBusy,
        styleClass: 'restore-item',
        command: () => this.restoreAppraisal(appraisal)
      });
    }

    if (this.canDeleteArchivedAppraisalRow(appraisal)) {
      items.push({
        label: this.translate.instant('DELETE'),
        icon: 'pi pi-trash kuba-icon-danger',
        disabled: isRowBusy,
        styleClass: 'danger-item',
        command: () => this.deleteArchivedAppraisal(appraisal)
      });
    }

    return items;
  }

  deleteArchivedAppraisal(appraisal: AppraisalMeetingListItemDto): void {
    if (!this.canDeleteArchivedAppraisalRow(appraisal) || this.isDeletingAppraisal(appraisal.Id)) {
      return;
    }

    const employeeName = this.getParticipantDisplayName(appraisal.EmployeeName, appraisal.EmployeeUserId);

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_DELETE_ARCHIVED_APPRAISAL', { name: employeeName }),
      header: this.translate.instant('DELETE_CONFIRMATION') || this.translate.instant('DELETE'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deletingAppraisalId = appraisal.Id;
        this.meetingService.deleteArchived(appraisal.Id).subscribe({
          next: () => {
            this.showSuccess(this.translate.instant('APPRAISAL_ARCHIVED_DELETE_SUCCESS'));
            this.loadAppraisals();
          },
          error: () => {
            this.deletingAppraisalId = null;
            this.showError(this.translate.instant('APPRAISAL_ARCHIVED_DELETE_ERROR'));
          }
        });
      }
    });
  }
  //#endregion

  //#region Helper Methods
  /**
   * Check user permissions
   */
  checkPermissions(): void {
    const role = BaseServices.UserRole;

    if (role === 'Admin' || role === 'Portal') {
      this.canCreateAppraisal = this.isManager;
      this.canEditAppraisal = true;
      return;
    }

    if (role === 'Editor') {
      this.canCreateAppraisal = this.isManager;
      this.canEditAppraisal = true;
      return;
    }

    this.canCreateAppraisal = false;
    this.canEditAppraisal = role === 'User' || role === 'Guest';
  }

  get effectiveViewScope(): AppraisalMeetingViewScope {
    return this.viewScope || (this.isManager ? 'manager' : 'employee');
  }

  get isEmployeeView(): boolean {
    return this.effectiveViewScope === 'employee';
  }

  get listTitleKey(): string {
    return this.isEmployeeView ? 'APPRAISAL_MY_MEETINGS' : 'APPRAISAL_MANAGER_MEETINGS';
  }

  get listTitleIconClass(): string {
    return this.isEmployeeView ? 'fa-user-circle-o' : 'fa-users';
  }

  get participantColumnLabelKey(): string {
    return this.isEmployeeView ? 'MANAGER' : 'EMPLOYEE_NAME';
  }

  get participantColumnIconClass(): string {
    return this.isEmployeeView ? 'fa-briefcase' : 'fa-user';
  }

  getParticipantName(appraisal: AppraisalMeetingListItemDto): string {
    return this.isEmployeeView
      ? this.getParticipantDisplayName(appraisal.ManagerName, appraisal.ManagerUserId)
      : this.getParticipantDisplayName(appraisal.EmployeeName, appraisal.EmployeeUserId);
  }

  getParticipantStatus(appraisal: AppraisalMeetingListItemDto): number | null | undefined {
    return this.isEmployeeView
      ? appraisal.ManagerUserStatus
      : appraisal.EmployeeUserStatus;
  }

  canArchiveAppraisalRow(appraisal: AppraisalMeetingListItemDto): boolean {
    return this.canManageArchiveState(appraisal) && !appraisal.IsArchived;
  }

  canRestoreAppraisalRow(appraisal: AppraisalMeetingListItemDto): boolean {
    return this.canManageArchiveState(appraisal) && appraisal.IsArchived;
  }

  canDeleteArchivedAppraisalRow(appraisal: AppraisalMeetingListItemDto): boolean {
    return ( !!appraisal?.IsArchived &&
      this.isManager &&
      appraisal.ManagerUserId === BaseServices.UserId
    );
  }

  private canManageArchiveState(appraisal: AppraisalMeetingListItemDto): boolean {
    if (!appraisal || !this.isManager) {
      return false;
    }

    if (BaseServices.UserRole === 'Admin') {
      return true;
    }

    if (appraisal.CreatedBy !== null && appraisal.CreatedBy !== undefined) {
      return appraisal.CreatedBy === BaseServices.UserId;
    }

    return appraisal.ManagerUserId === BaseServices.UserId;
  }

  isDeletingAppraisal(appraisalId: number): boolean {
    return this.deletingAppraisalId === appraisalId;
  }

  getParticipantDisplayName(name?: string | null, userId?: number | null): string {
    const trimmedName = name?.trim();
    if (trimmedName) {
      return trimmedName;
    }

    return userId ? `#${userId}` : '—';
  }

  getParticipantStatusLabelKey(status?: number | null): string | null {
    if (status === 0) {
      return 'DELETED';
    }

    if (status === 2) {
      return 'INACTIVE';
    }

    return null;
  }

  getParticipantStatusClass(status?: number | null): string {
    return status === 0 ? 'badge-danger' : 'badge-warning';
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: number): string {
    const statusMap: { [key: number]: string } = {
      [AppraisalMeetingStatus.Draft]: 'badge-warning',
      [AppraisalMeetingStatus.InProgress]: 'badge-secondary',
      [AppraisalMeetingStatus.PendingReview]: 'badge-primary',
      [AppraisalMeetingStatus.Completed]: 'badge-success',
      [AppraisalMeetingStatus.Cancelled]: 'badge-danger'
    };
    return statusMap[status] ?? 'badge-secondary';
  }

  getDisplayStatusClass(appraisal: AppraisalMeetingListItemDto): string {
    return appraisal.IsArchived ? 'badge-archived' : this.getStatusClass(appraisal.Status);
  }

  /**
   * Get status display text
   */
  getStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      [AppraisalMeetingStatus.Draft]: 'APPRAISAL_STATUS_PENDING_ACCESS',
      [AppraisalMeetingStatus.InProgress]: 'APPRAISAL_STATUS_PRE_MEETING',
      [AppraisalMeetingStatus.PendingReview]: 'APPRAISAL_STATUS_MEETING_ACCEPTED',
      [AppraisalMeetingStatus.Completed]: 'APPRAISAL_STATUS_MEETING_COMPLETED',
      [AppraisalMeetingStatus.Cancelled]: 'APPRAISAL_STATUS_CANCELLED_MEETING'
    };
    return statusMap[status] ?? '';
  }

  getDisplayStatusText(appraisal: AppraisalMeetingListItemDto): string {
    return appraisal.IsArchived ? 'ARCHIVED' : this.getStatusText(appraisal.Status);
  }

  getEmployeeProgressLabel(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived) {
      return 'ARCHIVED';
    }

    if (appraisal.Status === 3 || appraisal.Status === 4) {
      return this.getStatusText(appraisal.Status);
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_WAITING_FOR_ACCESS_APPROVAL';
    }

    switch (appraisal.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'APPRAISAL_WAITING_FOR_MANAGER';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'APPRAISAL_SAVED_FOR_LATER';
      default:
        return 'NOT_STARTED';
    }
  }

  getEmployeeProgressClass(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived) {
      return 'badge-archived';
    }

    if (appraisal.Status === 3) {
      return 'badge-success';
    }

    if (appraisal.Status === 4) {
      return 'badge-danger';
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'badge-warning';
    }

    switch (appraisal.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'badge-warning';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'badge-primary';
      default:
        return 'badge-secondary';
    }
  }

  getEmployeeProgressPercent(appraisal: AppraisalMeetingListItemDto): number {
    if (!appraisal.EmployeeRequiredQuestionCount) {
      return appraisal.EmployeeHasSavedResponses ? 100 : 0;
    }

    return Math.min(
      100,
      Math.round((appraisal.EmployeeAnsweredRequiredQuestionCount / appraisal.EmployeeRequiredQuestionCount) * 100)
    );
  }

  shouldShowEmployeeProgress(appraisal: AppraisalMeetingListItemDto): boolean {
    return ( appraisal.Status !== AppraisalMeetingStatus.Completed &&
      appraisal.EmployeeRequiredQuestionCount > 0
    );
  }

  getEmployeeProgressHintKey(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived) {
      return 'APPRAISAL_ARCHIVED_HINT';
    }

    if (appraisal.Status === 3) {
      return 'APPRAISAL_PROGRESS_HINT_COMPLETED';
    }

    if (appraisal.Status === 4) {
      return 'APPRAISAL_PROGRESS_HINT_CANCELLED';
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_MEETING_WAITING_FOR_ACCESS';
    }

    switch (appraisal.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'APPRAISAL_PROGRESS_HINT_WAITING_FOR_MANAGER';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'APPRAISAL_PROGRESS_HINT_SAVED_FOR_LATER';
      default:
        return 'APPRAISAL_PROGRESS_HINT_NOT_STARTED';
    }
  }

  getEmployeeProgressTimestamp(appraisal: AppraisalMeetingListItemDto): string | undefined {
    if (appraisal.IsArchived) {
      return this.toUtcTimestamp(appraisal.ArchivedOn);
    }

    if (appraisal.Status === 3) {
      return this.toUtcTimestamp(appraisal.CompletedDate);
    }

    switch (appraisal.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return this.toUtcTimestamp(appraisal.EmployeeSubmittedAt);
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return this.toUtcTimestamp(appraisal.EmployeeLastSavedAt);
      default:
        return undefined;
    }
  }

  toUtcTimestamp(value?: string): string | undefined {
    if (!value?.trim()) {
      return undefined;
    }

    const normalizedValue = value.trim();
    return this.timezoneSuffixPattern.test(normalizedValue)
      ? normalizedValue
      : `${normalizedValue}Z`;
  }

  getEmployeeProgressTimestampLabelKey(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived) {
      return 'ARCHIVED_ON';
    }

    if (appraisal.Status === 3) {
      return 'COMPLETED_DATE';
    }

    return appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted
      ? 'APPRAISAL_SUBMITTED_ON'
      : 'APPRAISAL_LAST_SAVED';
  }

  getEmployeePrimaryActionLabel(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_VIEW_PENDING_MEETING';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress &&
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'APPRAISAL_START_MEETING_BUTTON';
    }

    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4 ||
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'APPRAISAL_VIEW_SUMMARY';
    }

    return appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.NotStarted
      ? 'APPRAISAL_START_MEETING'
      : 'APPRAISAL_CONTINUE_MEETING';
  }

  getEmployeePrimaryActionIcon(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'fa-lock';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress &&
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'fa-play';
    }

    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4 ||
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'fa-eye';
    }

    return appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.NotStarted
      ? 'fa-play'
      : 'fa-chevron-right';
  }

  getEmployeePrimaryActionClass(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'employee-primary-action-summary';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress &&
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'employee-primary-action-start';
    }

    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4 ||
      appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'employee-primary-action-summary';
    }

    return appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.NotStarted
      ? 'employee-primary-action-start'
      : 'employee-primary-action-continue';
  }

  openPrimaryEmployeeAction(appraisal: AppraisalMeetingListItemDto): void {
    this.viewAppraisal(appraisal.Id);
  }

  getManagerPrimaryActionLabel(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4) {
      return 'APPRAISAL_VIEW_SUMMARY';
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_START_MEETING';
    }

    if (appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'APPRAISAL_START_MEETING_BUTTON';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress) {
      return 'APPRAISAL_WAITING_FOR_ANSWERS';
    }

    return 'APPRAISAL_CONTINUE_MEETING';
  }

  getManagerPrimaryActionIcon(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4) {
      return 'fa-eye';
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'fa-play';
    }

    if (appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'fa-play';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress) {
      return 'fa-clock-o';
    }

    return 'fa-chevron-right';
  }

  getManagerPrimaryActionClass(appraisal: AppraisalMeetingListItemDto): string {
    if (appraisal.IsArchived || appraisal.Status === 3 || appraisal.Status === 4) {
      return 'employee-primary-action-summary';
    }

    if (appraisal.Status === AppraisalMeetingStatus.Draft) {
      return 'employee-primary-action-start';
    }

    if (appraisal.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted) {
      return 'employee-primary-action-start';
    }

    if (appraisal.Status === AppraisalMeetingStatus.InProgress) {
      return 'employee-primary-action-waiting';
    }

    return 'employee-primary-action-continue';
  }

  openPrimaryManagerAction(appraisal: AppraisalMeetingListItemDto): void {
    this.viewAppraisal(appraisal.Id);
  }

  /**
   * Show success message
   */
  showSuccess(message: string): void {
    const summary = this.translate.instant('SUCCESS');
    this.notifications.add({ severity: 'success', summary: summary && summary !== 'SUCCESS' ? summary : 'Success', detail: message });
  }

  get hasActiveStatusFilter(): boolean {
    return this.statusFilter !== '';
  }

  getArchiveStatusCount(option: AppraisalArchiveStatusOption): number {
    return this.archiveStatusCounts[option.countKey] || 0;
  }

  getArchiveStatusButtonStyle(option: AppraisalArchiveStatusOption): Record<string, string> {
    const palette = this.getArchiveStatusPalette(option.value);
    const isSelected = this.archiveStatusFilter === option.value;

    return {
      display: 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      gap: '8px',
      'min-height': '36px',
      padding: '7px 13px',
      border: `1px solid ${isSelected ? palette.border : palette.mutedBorder}`,
      'border-radius': '8px',
      'background-color': isSelected ? palette.background : '#ffffff',
      color: isSelected ? palette.color : palette.mutedColor,
      'font-weight': '600',
      'letter-spacing': '0',
      'box-shadow': isSelected ? palette.shadow : 'none',
      'white-space': 'normal'
    };
  }

  getArchiveStatusCountStyle(option: AppraisalArchiveStatusOption): Record<string, string> {
    const palette = this.getArchiveStatusPalette(option.value);
    const isSelected = this.archiveStatusFilter === option.value;

    return {
      display: 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'min-width': '24px',
      height: '20px',
      padding: '0 7px',
      'border-radius': '8px',
      'background-color': isSelected ? palette.border : palette.countBackground,
      color: isSelected ? '#ffffff' : palette.color,
      'font-size': '12px',
      'font-weight': '700',
      'line-height': '1'
    };
  }

  private updateArchiveStatusCounts(metadata?: any): void {
    const activeCount = metadata?.ActiveCount ?? metadata?.activeCount;
    const archivedCount = metadata?.ArchivedCount ?? metadata?.archivedCount;
    const allCount = metadata?.AllCount ?? metadata?.allCount;

    this.archiveStatusCounts = {
      active: activeCount ??
        (this.archiveStatusFilter === 'active' ? this.totalItems : this.archiveStatusCounts.active),
      archived: archivedCount ??
        (this.archiveStatusFilter === 'archived' ? this.totalItems : this.archiveStatusCounts.archived),
      all: allCount ??
        (this.archiveStatusFilter === 'all' ? this.totalItems : this.archiveStatusCounts.all)
    };
  }

  private loadArchiveStatusCounts(baseFilter: AppraisalMeetingPaginatedFilterDto): void {
    this.archiveCountsSubscription?.unsubscribe();

    const buildCountFilter = (
      archiveStatus: AppraisalArchiveStatusFilter
    ): AppraisalMeetingPaginatedFilterDto => ({
      ...baseFilter,
      pageNumber: 1,
      pageSize: 1,
      sortColumn: 'CreatedOn',
      sortDirection: 'DESC',
      archiveStatus
    });

    this.archiveCountsSubscription = forkJoin([
      this.meetingService.getPagedList(BaseServices.BusinessId, buildCountFilter('active')),
      this.meetingService.getPagedList(BaseServices.BusinessId, buildCountFilter('archived')),
      this.meetingService.getPagedList(BaseServices.BusinessId, buildCountFilter('all'))
    ]).subscribe({
      next: ([activeResponse, archivedResponse, allResponse]) => {
        this.archiveStatusCounts = {
          active: activeResponse?.TotalCount || 0,
          archived: archivedResponse?.TotalCount || 0,
          all: allResponse?.TotalCount || 0
        };
      },
      error: () => {
        // Keep the main list usable if the sidecar count requests fail.
      }
    });
  }

  private buildAppraisalFilter(): AppraisalMeetingPaginatedFilterDto {
    return {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortColumn: this.sortField,
      sortDirection: this.sortOrder as 'ASC' | 'DESC',
      employeeName: this.employeeNameFilter || undefined,
      templateName: this.templateNameFilter || undefined,
      status: this.statusFilter !== '' ? +this.statusFilter : undefined,
      archiveStatus: this.archiveStatusFilter,
      viewScope: this.effectiveViewScope
    };
  }

  private applyArchiveStatusDefaultSort(): void {
    if (this.archiveStatusFilter === 'archived') {
      this.sortField = 'ScheduledDate';
      this.sortOrder = 'DESC';
      return;
    }

    this.sortField = this.archiveStatusFilter === 'all'
      ? 'ActiveFirstMeetingActionPriority'
      : 'MeetingActionPriority';
    this.sortOrder = 'ASC';
  }

  private getArchiveStatusPalette(value: AppraisalArchiveStatusFilter): AppraisalArchiveStatusPalette {
    const palettes: Record<AppraisalArchiveStatusFilter, AppraisalArchiveStatusPalette> = {
      active: {
        background: '#e6f4f1',
        border: '#0f766e',
        color: '#0f766e',
        mutedBorder: '#b7d6d1',
        mutedColor: '#0f766e',
        countBackground: '#d5ebe7',
        shadow: '0 6px 14px rgba(15, 118, 110, 0.14)'
      },
      archived: {
        background: '#eef2f7',
        border: '#64748b',
        color: '#334155',
        mutedBorder: '#cbd5e1',
        mutedColor: '#475569',
        countBackground: '#e2e8f0',
        shadow: '0 6px 14px rgba(71, 85, 105, 0.14)'
      },
      all: {
        background: '#e0f2fe',
        border: '#0284c7',
        color: '#075985',
        mutedBorder: '#bae6fd',
        mutedColor: '#0369a1',
        countBackground: '#d9effc',
        shadow: '0 6px 14px rgba(2, 132, 199, 0.14)'
      }
    };

    return palettes[value];
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    const summary = this.translate.instant('ERROR');
    this.notifications.add({ severity: 'error', summary: summary && summary !== 'ERROR' ? summary : 'Error', detail: message });
  }

  private getDownloadFileName(response: HttpResponse<Blob>): string {
    const contentDisposition = response.headers.get('content-disposition');
    const fallbackName = 'dialog-meeting-report.pdf';

    if (!contentDisposition) {
      return fallbackName;
    }

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim());
      } catch {
        return utf8Match[1].trim();
      }
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1].trim();
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (plainMatch?.[1]) {
      return plainMatch[1].trim();
    }

    return fallbackName;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener';
    link.click();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  private buildPrintHtml(meeting: AppraisalMeetingDto): string {
    const labels = {
      dialogMeeting: this.translate.instant('APPRAISAL'),
      title: this.translate.instant('TITLE'),
      template: this.translate.instant('FORM'),
      status: this.translate.instant('STATUS'),
      scheduledDate: this.translate.instant('SCHEDULED_DATE'),
      completedDate: this.translate.instant('COMPLETED_DATE'),
      manager: this.translate.instant('MANAGER'),
      employee: this.translate.instant('EMPLOYEE'),
      notes: this.translate.instant('APPRAISAL_MEETING_NOTES'),
      questions: this.translate.instant('QUESTIONS'),
      question: this.translate.instant('QUESTION'),
      questionType: this.translate.instant('QUESTION_TYPE'),
      yes: this.translate.instant('YES'),
      no: this.translate.instant('NO')
    };

    const orderedSections = [...(meeting.Sections || [])].sort(
      (a, b) => a.SortOrder - b.SortOrder
    );

    const sectionsHtml = orderedSections
      .map((section, sectionIndex) => {
        const orderedQuestions = [...(section.Questions || [])].sort(
          (a, b) => a.SortOrder - b.SortOrder
        );

        const questionsHtml = orderedQuestions
          .map((question, questionIndex) => {
            const managerAnswer = this.getPrintAnswerValue(
              question,
              AppraisalMeetingRespondentRole.Manager,
              labels.yes,
              labels.no
            );
            const employeeAnswer = this.getPrintAnswerValue(
              question,
              AppraisalMeetingRespondentRole.Employee,
              labels.yes,
              labels.no
            );

            return `
              <article class="question-card">
                <div class="question-title-row">
                  <span class="question-index">${this.escapeHtml(
                    `${labels.question} ${questionIndex + 1}`
                  )}</span>
                  <span class="question-type">${this.escapeHtml(
                    `${labels.questionType}: ${this.getQuestionTypeLabel(question.QuestionType)}`
                  )}</span>
                </div>
                <h4>${this.escapeHtml(question.QuestionText || '—')}</h4>
                <div class="answers-grid">
                  <div class="answer-card">
                    <div class="answer-label">${this.escapeHtml(labels.manager)}</div>
                    <div class="answer-value">${managerAnswer}</div>
                  </div>
                  <div class="answer-card">
                    <div class="answer-label">${this.escapeHtml(labels.employee)}</div>
                    <div class="answer-value">${employeeAnswer}</div>
                  </div>
                </div>
              </article>
            `;
          })
          .join('');

        return `
          <section class="section-card">
            <header class="section-header">
              <h3>${this.escapeHtml(`${sectionIndex + 1}. ${section.Title || '—'}`)}</h3>
              <span class="section-count">${this.escapeHtml(
                `${orderedQuestions.length} ${labels.questions}`
              )}</span>
            </header>
            ${
              section.Description
                ? `<p class="section-description">${this.escapeMultiline(section.Description)}</p>`
                : ''
            }
            ${questionsHtml || `<p class="empty">—</p>`}
          </section>
        `;
      })
      .join('');

    const statusText = this.translate.instant(this.getStatusText(meeting.Status));
    const docTitle = labels.dialogMeeting;

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${this.escapeHtml(docTitle)}</title>
          <style>
            @page { size: A4; margin: 16mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              color: #111827;
              font-size: 12px;
              line-height: 1.45;
              background: #ffffff;
            }
            .report {
              max-width: 1000px;
              margin: 0 auto;
              padding: 18px 20px 28px;
            }
            .header {
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 12px;
              margin-bottom: 14px;
            }
            .header h1 {
              margin: 0;
              font-size: 20px;
              font-weight: 700;
              color: #111827;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px 14px;
              margin-bottom: 16px;
            }
            .summary-item {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 8px 10px;
              page-break-inside: avoid;
            }
            .summary-label {
              font-size: 10px;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.25px;
            }
            .summary-value {
              margin-top: 2px;
              font-size: 13px;
              font-weight: 600;
              color: #111827;
              word-break: break-word;
            }
            .meeting-notes {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 10px;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .section-card {
              border: 1px solid #dbe3ee;
              border-radius: 10px;
              padding: 10px 12px;
              margin-bottom: 14px;
              page-break-inside: avoid;
            }
            .section-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 8px;
              margin-bottom: 4px;
            }
            .section-header h3 {
              margin: 0;
              font-size: 15px;
              color: #0f172a;
            }
            .section-count {
              font-size: 11px;
              color: #475569;
              white-space: nowrap;
            }
            .section-description {
              margin: 6px 0 8px;
              color: #475569;
            }
            .manager-notes {
              margin-bottom: 10px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 8px 10px;
            }
            .question-card {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 8px 10px;
              margin-bottom: 8px;
            }
            .question-title-row {
              display: flex;
              justify-content: space-between;
              gap: 8px;
              margin-bottom: 4px;
              font-size: 11px;
              color: #64748b;
            }
            .question-card h4 {
              margin: 0 0 8px;
              font-size: 13px;
              color: #111827;
            }
            .answers-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px;
            }
            .answer-card {
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 7px 8px;
              background: #fafafa;
              min-height: 46px;
            }
            .answer-label {
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.2px;
              color: #6b7280;
              margin-bottom: 3px;
            }
            .answer-value {
              color: #111827;
              word-break: break-word;
            }
            .empty {
              color: #6b7280;
              margin: 0;
            }
            @media print {
              .section-card { break-inside: avoid; }
              .summary-item { break-inside: avoid; }
              .question-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <main class="report">
            <header class="header">
              <h1>${this.escapeHtml(labels.dialogMeeting)}</h1>
            </header>

            <section class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.title)}</div>
                <div class="summary-value">${this.escapeHtml(meeting.Title || '—')}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.status)}</div>
                <div class="summary-value">${this.escapeHtml(statusText || '—')}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.template)}</div>
                <div class="summary-value">${this.escapeHtml(meeting.TemplateName || '—')}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.scheduledDate)}</div>
                <div class="summary-value">${this.escapeHtml(this.formatDate(meeting.ScheduledDate, false))}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.manager)}</div>
                <div class="summary-value">${this.escapeHtml(meeting.ManagerName || '—')}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.employee)}</div>
                <div class="summary-value">${this.escapeHtml(meeting.EmployeeName || '—')}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(labels.completedDate)}</div>
                <div class="summary-value">${this.escapeHtml(this.formatDate(this.toUtcTimestamp(meeting.CompletedDate), false))}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">${this.escapeHtml(this.translate.instant('CREATED_ON'))}</div>
                <div class="summary-value">${this.escapeHtml(this.formatDate(this.toUtcTimestamp(meeting.CreatedOn), true))}</div>
              </div>
            </section>

            ${
              meeting.Notes
                ? `
                  <section class="meeting-notes">
                    <strong>${this.escapeHtml(labels.notes)}:</strong>
                    <div>${this.escapeMultiline(meeting.Notes)}</div>
                  </section>
                `
                : ''
            }

            ${sectionsHtml}
          </main>
        </body>
      </html>
    `;
  }

  private renderPrintLoading(printWindow: Window): void {
    const printLabel = this.escapeHtml(this.translate.instant('PRINT') || 'Print');
    const loadingLabel = this.escapeHtml(this.translate.instant('LOADING') || 'Loading...');

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${printLabel}</title>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 24px; color:#475569;">
          <div style="display:flex;align-items:center;gap:8px;">
            <span>${loadingLabel}</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  }

  private renderPrintError(printWindow: Window, message: string): void {
    const errorTitle = this.escapeHtml(this.translate.instant('ERROR') || 'Error');
    const errorMessage = this.escapeHtml(message || 'Unable to prepare print document.');

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${errorTitle}</title>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 24px; color:#991b1b;">
          <h3 style="margin: 0 0 8px;">${errorTitle}</h3>
          <p style="margin: 0;">${errorMessage}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
  }

  private getPrintAnswerValue(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole,
    yesLabel: string,
    noLabel: string
  ): string {
    const response = this.getResponseByRole(question, role);
    if (!response) {
      return '—';
    }

    if (question.QuestionType === 1) {
      return this.escapeMultiline(response.TextAnswer || '');
    }

    if (question.QuestionType === 2) {
      if (response.YesNoAnswer === true) {
        return this.escapeHtml(yesLabel);
      }
      if (response.YesNoAnswer === false) {
        return this.escapeHtml(noLabel);
      }
      return '—';
    }

    if (question.QuestionType === 0) {
      if (response.LikertAnswer === null || response.LikertAnswer === undefined) {
        return '—';
      }

      const low = question.LikertLowLabel?.trim();
      const high = question.LikertHighLabel?.trim();
      const rangeLabel =
        low || high
          ? ` (${this.escapeHtml(low || this.translate.instant('DISAGREE'))} - ${this.escapeHtml(
              high || this.translate.instant('AGREE')
            )})`
          : '';

      return `${this.escapeHtml(String(response.LikertAnswer))}/5${rangeLabel}`;
    }

    return '—';
  }

  private getResponseByRole(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): AppraisalMeetingResponseSubmitDto | undefined {
    return (question.Responses || []).find(response => response.RespondentRole === role);
  }

  private getQuestionTypeLabel(questionType: number): string {
    if (questionType === 0) {
      return this.translate.instant('LIKERT_5_SCALE');
    }
    if (questionType === 1) {
      return this.translate.instant('FREE_TEXT');
    }
    if (questionType === 2) {
      return this.translate.instant('YES_NO');
    }
    return this.translate.instant('UNKNOWN');
  }

  private formatDate(value?: string, includeTime = false): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    const locale = BaseServices.userCultureInfo?.() || 'en-GB';
    const options: Intl.DateTimeFormatOptions = includeTime
      ? { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: 'short', day: '2-digit' };

    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeMultiline(value: string): string {
    if (!value?.trim()) {
      return '—';
    }

    return this.escapeHtml(value).replace(/\n/g, '<br/>');
  }

  private initializeStatusOptions(): void {
    this.statusOptions = [
      { label: this.translate.instant('ALL_STATUSES'), value: '' },
      { label: this.translate.instant('APPRAISAL_STATUS_PENDING_ACCESS'), value: '0' },
      { label: this.translate.instant('APPRAISAL_STATUS_PRE_MEETING'), value: '1' },
      { label: this.translate.instant('APPRAISAL_STATUS_MEETING_ACCEPTED'), value: '2' },
      { label: this.translate.instant('APPRAISAL_STATUS_MEETING_COMPLETED'), value: '3' },
      { label: this.translate.instant('APPRAISAL_STATUS_CANCELLED_MEETING'), value: '4' }
    ];
  }
  //#endregion
}
